import { router } from "@inertiajs/react";
import { useMediaQuery } from "@uidotdev/usehooks";
import {
	CalendarIcon,
	Info,
	Lock,
	Pencil,
	Plus,
	TriangleAlert,
	X,
	Zap,
} from "lucide-react";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	type PendingKind,
	railsEntries,
	type Timeline,
	type TimelineEntry,
} from "@/lib/therapist-relocation";
import type { TherapistFormSchema } from "@/lib/therapists";
import { cn } from "@/lib/utils";
import type { Location } from "@/types/admin-portal/location";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import type { TherapistAddress } from "@/types/admin-portal/therapist";
import { AddressTimelineBlock, Tag } from "./address-steps";
import { shortCity, type UpcomingVisit } from "./address-timeline";
import { CancelRelocationDialog } from "./cancel-relocation-dialog";
import { CorrectAddressDialog } from "./correct-address-dialog";
import { RescheduleRelocationDialog } from "./reschedule-relocation-dialog";
import {
	ScheduleRelocationDialog,
	type ScheduleTarget,
} from "./schedule-relocation-dialog";

type Draft = TherapistFormSchema["addresses"][number];
export type PendingChange = { kind: PendingKind; row: Draft };

const NEW_ROW = "new-row";
// unsaved drafts take the reserve orange, the `secondary` variant
const PENDING_TAG = "border-secondary bg-secondary/10 text-secondary";

/**
 * The edit form's Alamat section (#245 Bagian 3): Go's garis waktu and Tangga, whose actions write to
 * Go at once, and a Rak of Alamat Lain, whose changes wait for the form's Simpan (X3, X13).
 */
export function AddressSection({
	addresses,
	saved,
	pending,
	relocationsPath,
	relocationSupported,
	canManage,
	timeline,
	upcomingVisits = [],
	locations,
	onGoSaved,
	onDiscardRequest,
	renderEditor,
}: {
	/** Every address of the therapist, as the edit page serializes them. */
	addresses: TherapistAddress[];
	/** The Alamat Lain rows as last saved, which Batal in the edit panel returns to. */
	saved: Draft[];
	pending: PendingChange[];
	relocationsPath: string;
	relocationSupported: boolean;
	/** False for an admin without role ADMIN in Go, whose writes Go would refuse with 403 (X9). */
	canManage: boolean;
	timeline?: ExternalProp<Timeline>;
	/** The visits Go's conflict check reads, for the garis waktu's dots; deferred. */
	upcomingVisits?: UpcomingVisit[];
	locations: Pick<Location, "id" | "city" | "state" | "country">[];
	/** Reloads the page after a Go write. */
	onGoSaved: () => void;
	onDiscardRequest: () => void;
	/** The address fields of the Alamat Lain at this index. */
	renderEditor: (index: number) => ReactNode;
}) {
	const { t } = useTranslation("therapists", { keyPrefix: "form.address" });
	const form = useFormContext<TherapistFormSchema>();
	const others = useFieldArray({ control: form.control, name: "addresses" });
	const narrow = useMediaQuery("(max-width: 639px)");

	const [openKey, setOpenKey] = useState<string | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
	const [confirmToday, setConfirmToday] = useState<number | null>(null);
	const [scheduling, setScheduling] = useState<ScheduleTarget | null>(null);
	const [correcting, setCorrecting] = useState<TimelineEntry | null>(null);
	const [rescheduling, setRescheduling] = useState<TimelineEntry | null>(null);
	const [cancelling, setCancelling] = useState<TimelineEntry | null>(null);

	// X6: once a therapist has a dated row, every change of Alamat Utama is an Utama Mulai.
	const hasDated = addresses.some(
		(row) => row.effectiveFrom || row.effectiveUntil,
	);
	// the dialogs act on Go's rows only; before 0115 nothing is dated, so no day decides a status
	const entries = relocationSupported
		? (timeline?.data?.entries ?? [])
		: railsEntries(addresses, "");
	const current = entries.find((entry) => entry.status === "CURRENT");
	// Go's writes reload the page, which would overwrite unsaved Alamat Lain drafts.
	const locked = pending.length > 0;
	const goActions = relocationSupported && canManage;
	const todayPicked = pending.some((change) => change.kind === "today");
	const values = form.watch("addresses") ?? [];
	const kindOf = (row: Draft): PendingKind | undefined =>
		row.id == null
			? "new"
			: pending.find(
					(change) =>
						change.kind !== "delete" &&
						String(change.row.id) === String(row.id),
				)?.kind;
	const removed = pending.filter((change) => change.kind === "delete");
	const openIndex = others.fields.findIndex((field) => field.id === openKey);

	const lockTitle = locked ? t("locked") : undefined;
	const addressIdOf = (entry: TimelineEntry) =>
		addresses.find((row) => row.therapistAddressId === entry.therapistAddressId)
			?.id;

	const entryActions = (entry: TimelineEntry) => {
		if (!goActions)
			return entry.status === "ENDED" ? (
				<span className="flex items-center gap-1 text-xs text-muted-foreground">
					<Lock className="size-3" />
					{t("history")}
				</span>
			) : null;
		const buttons =
			entry.status === "ENDED"
				? addressIdOf(entry) && (
						<Button
							type="button"
							variant="outline"
							size="xs"
							disabled={locked}
							title={lockTitle}
							onClick={() => {
								const addressId = addressIdOf(entry);
								if (addressId)
									setScheduling({
										addressId,
										city: entry.address.city,
										address: entry.address.address,
										postalCode: entry.address.postalCode,
									});
							}}
						>
							<CalendarIcon />
							{t("actions.start")}
						</Button>
					)
				: [
						<Button
							key="fix"
							type="button"
							variant="outline"
							size="xs"
							disabled={locked}
							title={lockTitle}
							onClick={() => setCorrecting(entry)}
						>
							<Pencil />
							{t("actions.fix")}
						</Button>,
						entry.cancellable && (
							<Button
								key="date"
								type="button"
								variant="outline"
								size="xs"
								disabled={locked}
								title={lockTitle}
								onClick={() => setRescheduling(entry)}
							>
								<CalendarIcon />
								{t("actions.reschedule")}
							</Button>
						),
						entry.cancellable && (
							<Button
								key="cancel"
								type="button"
								variant="ghost"
								size="xs"
								disabled={locked}
								title={lockTitle}
								className="text-destructive"
								onClick={() => setCancelling(entry)}
							>
								<X />
								{t("actions.cancel")}
							</Button>
						),
					];
		return <div className="flex flex-wrap gap-2">{buttons}</div>;
	};

	const pendingLabel = (kind: PendingKind) =>
		`${t(`pending.${kind}`)} · ${t("pending.suffix")}`;
	const placeOf = (row: Draft) => shortCity(row.city) || t("panel.new_address");
	const changeList = pending
		.map(({ kind, row }) => `${placeOf(row)} (${t(`pending_what.${kind}`)})`)
		.join(", ");

	const closePanel = async () => {
		if (openIndex === -1) return setOpenKey(null);
		if (await form.trigger(`addresses.${openIndex}`)) setOpenKey(null);
	};
	const revertPanel = () => {
		if (openIndex === -1) return setOpenKey(null);
		const row = values[openIndex];
		const before = saved.find(
			(savedRow) => row?.id != null && String(savedRow.id) === String(row.id),
		);
		if (before) others.update(openIndex, before);
		else others.remove(openIndex);
		setOpenKey(null);
	};

	const panel = () =>
		openIndex === -1 ? null : (
			<div className="grid gap-4 p-4 border col-span-full bg-card">
				<p className="text-sm">
					<b>
						{t(
							values[openIndex]?.id == null
								? "panel.add_title"
								: "panel.edit_title",
						)}
					</b>{" "}
					<span className="text-xs text-muted-foreground">
						· {t("panel.hint")}
					</span>
				</p>
				{renderEditor(openIndex)}
				<div className="flex justify-end gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={revertPanel}
					>
						{t("panel.revert")}
					</Button>
					<Button type="button" size="sm" onClick={closePanel}>
						{t("panel.close")}
					</Button>
				</div>
			</div>
		);

	// * Rak: one tile per Alamat Lain (X12, X13)
	const shelf = () => (
		<>
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{others.fields.map((field, index) => {
					const row = values[index];
					if (!row) return null;
					const kind = kindOf(row);
					const isOpen = field.id === openKey;
					return (
						<Fragment key={field.id}>
							<div
								className={cn(
									"flex flex-col gap-3 p-3 border bg-card",
									isOpen && "border-primary ring-1 ring-primary",
									kind && "border-secondary",
								)}
							>
								<div className="grid flex-1 gap-0.5 text-sm">
									{kind && (
										<Tag className={cn(PENDING_TAG, "mb-1 w-fit")}>
											{pendingLabel(kind)}
										</Tag>
									)}
									<span className="font-semibold uppercase">
										{shortCity(row.city) || "—"}
									</span>
									<span className="text-xs break-words text-muted-foreground">
										{row.address}
									</span>
									{row.postalCode && (
										<span className="text-xs text-muted-foreground">
											{row.postalCode}
										</span>
									)}
								</div>
								<div className="flex items-center gap-1 pt-2 border-t">
									{kind === "today" ? (
										<Button
											type="button"
											variant="outline"
											size="xs"
											onClick={() =>
												form.setValue(`addresses.${index}.active`, false)
											}
										>
											{t("actions.undo")}
										</Button>
									) : (
										<>
											{goActions && row.id != null && (
												<Button
													type="button"
													variant="outline"
													size="xs"
													disabled={locked}
													title={lockTitle}
													onClick={() => {
														const savedRow = addresses.find(
															(address) =>
																String(address.id) === String(row.id),
														);
														if (savedRow)
															setScheduling({
																addressId: savedRow.id,
																city: savedRow.location.city,
																address: savedRow.address,
																postalCode: savedRow.postalCode,
															});
													}}
												>
													<CalendarIcon />
													{t("actions.start")}
												</Button>
											)}
											<span className="flex-1" />
											{!hasDated && !todayPicked && kind !== "new" && (
												<Button
													type="button"
													variant="ghost"
													size="xs"
													className="px-1.5"
													title={t("actions.today")}
													aria-label={t("actions.today")}
													onClick={() => setConfirmToday(index)}
												>
													<Zap />
												</Button>
											)}
											<Button
												type="button"
												variant="ghost"
												size="xs"
												className="px-1.5"
												title={t("actions.edit")}
												aria-label={t("actions.edit")}
												onClick={() => setOpenKey(isOpen ? null : field.id)}
											>
												<Pencil />
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="xs"
												className="px-1.5 text-destructive"
												title={t("actions.delete")}
												aria-label={t("actions.delete")}
												onClick={() => setConfirmDelete(index)}
											>
												<X />
											</Button>
										</>
									)}
								</div>
							</div>
							{/* on a phone the panel opens right under the tile being edited */}
							{narrow && isOpen && panel()}
						</Fragment>
					);
				})}
				{removed.map(({ row }) => (
					<div
						key={`removed-${row.id}`}
						className="flex flex-col gap-3 p-3 border border-secondary bg-muted/40"
					>
						<div className="grid flex-1 gap-0.5 text-sm text-muted-foreground">
							<Tag className={cn(PENDING_TAG, "mb-1 w-fit")}>
								{pendingLabel("delete")}
							</Tag>
							<span className="font-semibold uppercase line-through">
								{shortCity(row.city)}
							</span>
							<span className="text-xs line-through break-words">
								{row.address}
							</span>
						</div>
						<div className="pt-2 border-t">
							<Button
								type="button"
								variant="outline"
								size="xs"
								onClick={() => others.append(row)}
							>
								{t("actions.undo")}
							</Button>
						</div>
					</div>
				))}
				<button
					type="button"
					className="flex items-center justify-center min-h-28 gap-2 p-3 text-sm font-medium border border-dashed text-muted-foreground hover:border-primary hover:text-primary"
					onClick={() => {
						others.append({
							country: "INDONESIA",
							countryCode: "ID",
							state: "",
							city: "",
							postalCode: undefined,
							address: "",
							active: false,
							lat: null,
							lng: null,
							coordinates: "",
						});
						setOpenKey(NEW_ROW);
					}}
				>
					<Plus className="size-4" />
					{t("others.add")}
				</button>
			</div>
			{!narrow && panel()}
		</>
	);

	// the appended row's key is only known once useFieldArray has rendered it
	useEffect(() => {
		if (openKey === NEW_ROW && others.fields.length)
			setOpenKey(others.fields[others.fields.length - 1].id);
	}, [openKey, others.fields]);

	const deleting = confirmDelete == null ? undefined : values[confirmDelete];
	const promoting = confirmToday == null ? undefined : values[confirmToday];

	return (
		<div className="grid gap-5">
			<p className="text-sm text-muted-foreground">{t("description")}</p>
			<p className="flex gap-2 p-3 text-xs border bg-muted/40">
				<Zap className="size-4 shrink-0" />
				<span>
					{goActions
						? t(hasDated ? "two_saves" : "two_saves_today")
						: t(hasDated ? "draft_saves" : "draft_saves_today")}
				</span>
			</p>
			{relocationSupported && !canManage && (
				<p className="flex gap-2 text-xs text-muted-foreground">
					<Info className="size-4 shrink-0" />
					{t("no_access")}
				</p>
			)}

			<AddressTimelineBlock
				relocationSupported={relocationSupported}
				timeline={timeline}
				addresses={addresses}
				upcomingVisits={upcomingVisits}
				onRetry={() => router.reload({ only: ["timeline"] })}
				actions={entryActions}
			/>

			<div className="grid gap-3">
				<p className="text-sm font-semibold">
					{t("others.title")}{" "}
					<span className="font-normal text-muted-foreground">
						· {others.fields.length}
					</span>
				</p>
				{hasDated && (
					<p className="text-xs text-muted-foreground">{t("dated_hint")}</p>
				)}
				{shelf()}
				{pending.length > 0 && (
					<output className="flex flex-col gap-3 p-3 text-sm border border-secondary bg-secondary/10 sm:flex-row sm:items-start">
						<TriangleAlert className="size-4 shrink-0" />
						<span className="flex-1">
							{t("bar", { count: pending.length, list: changeList })}
						</span>
						<Button
							type="button"
							variant="outline"
							size="xs"
							onClick={onDiscardRequest}
						>
							{t("discard")}
						</Button>
					</output>
				)}
			</div>

			{/* Outside AddressTimelineBlock, so its loading state cannot unmount them. */}
			{goActions && (
				<>
					<ScheduleRelocationDialog
						target={scheduling}
						onClose={() => setScheduling(null)}
						onSaved={onGoSaved}
						relocationsPath={relocationsPath}
						entries={entries}
					/>
					<RescheduleRelocationDialog
						entry={rescheduling}
						entries={entries}
						onClose={() => setRescheduling(null)}
						onSaved={onGoSaved}
						relocationsPath={relocationsPath}
					/>
					<CancelRelocationDialog
						entry={cancelling}
						entries={entries}
						onClose={() => setCancelling(null)}
						onSaved={onGoSaved}
						relocationsPath={relocationsPath}
					/>
					<CorrectAddressDialog
						entry={correcting}
						onClose={() => setCorrecting(null)}
						onSaved={onGoSaved}
						relocationsPath={relocationsPath}
						locations={locations}
					/>
				</>
			)}

			<AlertDialog
				open={!!deleting}
				onOpenChange={(open) => !open && setConfirmDelete(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("delete_dialog.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{deleting &&
								t("delete_dialog.description", {
									address: [
										deleting.city,
										deleting.address,
										deleting.postalCode,
									]
										.filter(Boolean)
										.join(" · "),
								})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("dialog_back")}</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={() => {
								if (confirmDelete == null) return;
								if (others.fields[confirmDelete]?.id === openKey)
									setOpenKey(null);
								others.remove(confirmDelete);
								setConfirmDelete(null);
							}}
						>
							{t("delete_dialog.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={!!promoting}
				onOpenChange={(open) => !open && setConfirmToday(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("today_dialog.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{promoting &&
								t(
									current
										? "today_dialog.description"
										: "today_dialog.description_no_current",
									{
										address: [promoting.city, promoting.address]
											.filter(Boolean)
											.join(" · "),
										current: current?.address.city,
									},
								)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("dialog_back")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (confirmToday == null) return;
								form.setValue(`addresses.${confirmToday}.active`, true);
								setConfirmToday(null);
							}}
						>
							{t("today_dialog.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
